import { format } from 'date-fns';
import flow from 'lodash/flow';
import { Option } from 'types/general.types';
import { dateFormats } from 'app/constants';
import { AudioStatusReasonType, AudioStatusType, Audio } from 'types/audio';
import { ContributionFormData } from 'app/components/Song/Contributions/ContributionForm/types';
import {
  ContributionClassName,
  ContributionClassRolesDDL,
} from 'types/contribution';
import { AudioFormItem } from 'app/components/Song/Audio/AudioAddDialog/types';

import {
  emptyOptionToNull,
  emptyStringToNull,
  isForbiddenError,
  isNotFoundError,
} from 'app/utils/utils';
import { SongDetailsFormData } from 'app/components/Song/Details/SongDetailsForm/types';
import { SongFormData } from 'app/components/Song/SongForm/types';
import { LyricsData } from 'types/lyrics.types';
import { GenericObject } from 'types/generic.types';
import { AppState } from 'app/store';
import { getDisplayName, getInitials, getUserGuid } from '../Auth/selectors';
import { getSongGuidSelector } from './selectors';

const formatPatchDate = (date?: Date | null) =>
  date || date === null
    ? {
        date:
          date === null
            ? null
            : format(new Date(date), dateFormats.DATE_PICKER_FOCUSED),
      }
    : undefined;

export const getPatchContributionDataForBE = (
  dirtyValues: Partial<ContributionFormData>,
  className: ContributionClassName,
  profileGuid?: string
) => {
  let patchData: any = {
    name: dirtyValues.name,
    profileGuid: dirtyValues.profileGuid,
    ...(dirtyValues.location
      ? {
          location: {
            country:
              dirtyValues.location?.country === null
                ? null
                : dirtyValues.location?.country?.value,
            address: emptyStringToNull(dirtyValues.location?.address),
            name: emptyStringToNull(dirtyValues.location?.name),
          },
        }
      : {}),
    ...(dirtyValues.date
      ? {
          date: formatPatchDate(dirtyValues.date?.date),
        }
      : {}),
  };

  switch (className) {
    case ContributionClassName.ARTIST: {
      patchData = {
        ...patchData,
        ipn: profileGuid ? undefined : emptyStringToNull(dirtyValues.artistIpn),
        isni: profileGuid
          ? undefined
          : emptyStringToNull(dirtyValues.artistIsni),
        role: emptyOptionToNull(dirtyValues.artistRole),
      };
      break;
    }
    case ContributionClassName.SONGWRITER: {
      patchData = {
        ...patchData,
        ipi: profileGuid
          ? undefined
          : emptyStringToNull(dirtyValues.composerIpi),
        isni: profileGuid
          ? undefined
          : emptyStringToNull(dirtyValues.composerIsni),
        role: emptyOptionToNull(dirtyValues.composerRole),
      };
      break;
    }
    case ContributionClassName.PRODUCTION: {
      patchData = {
        ...patchData,
        ipn: profileGuid
          ? undefined
          : emptyStringToNull(dirtyValues.producerIpn),
        isni: profileGuid
          ? undefined
          : emptyStringToNull(dirtyValues.producerIsni),
        role: emptyOptionToNull(dirtyValues.productionRole),
      };
      break;
    }
    case ContributionClassName.PERFORMER: {
      patchData = {
        ...patchData,
        ipn: profileGuid
          ? undefined
          : emptyStringToNull(dirtyValues.performerIpn),
        isni: profileGuid
          ? undefined
          : emptyStringToNull(dirtyValues.performerIsni),
        role: emptyOptionToNull(dirtyValues.performerRole),
        roleDetail: emptyOptionToNull(dirtyValues.performerRoleDetail),
      };
      break;
    }
    default:
  }

  return patchData;
};

export const formatSongPatchData = (
  form: Partial<SongFormData & SongDetailsFormData>
) => ({
  ...form,
  title: form.title ? form.title.trim() : undefined,
  altTitle: emptyStringToNull(form.altTitle),
  displayArtistName: emptyStringToNull(form.displayArtistName),
  notes: emptyStringToNull(form.notes),
  explicit: form.explicit,
  tempo: emptyStringToNull(form.tempo as string),
  language: emptyOptionToNull(form.language),
  keySignature: emptyOptionToNull(form.keySignature),
  timeSignature: emptyOptionToNull(form.timeSignature),
  creationDate: formatPatchDate(form.creationDate),
  location:
    form.location?.address || form.location?.address === ''
      ? {
          address: emptyStringToNull(form.location?.address),
        }
      : undefined,
});

export const formatEditAudioFormData = (
  dirtyValues: Partial<AudioFormItem>
) => {
  const formattedValues: GenericObject<
    string | undefined | null | boolean | Option
  > = {};
  Object.entries(dirtyValues).forEach(([key, value]) => {
    formattedValues[key] = value === '' ? null : value;
  });
  return formattedValues;
};

export const getPopulatedLocalAudioInstance = (
  incompleteAudio: Audio,
  state: AppState
): Audio => ({
  ...incompleteAudio,
  createdBy: {
    guid: getUserGuid(state) || '',
    profileCard: {
      guid: getUserGuid(state) || '',
      subject: getUserGuid(state) || '',
      displayName: getDisplayName(state) || '',
      initials: getInitials(state) || '',
    },
  },
  parent: getSongGuidSelector(state) || '',
  status: AudioStatusType.FAILURE,
  statusReason: AudioStatusReasonType.RETRYABLE,
});

export const extractContributionClassesAndRolesDDLS = (
  classRoles: ContributionClassRolesDDL
): [ContributionClassRolesDDL, ContributionClassRolesDDL] => {
  const classesDDL = flow([
    Object.entries,
    (arr: Array<any>) => arr.filter(([, value]) => !value.parent),
    Object.fromEntries,
  ])(classRoles);
  const rolesDDL = flow([
    Object.entries,
    (arr: Array<any>) => arr.filter(([, value]) => Boolean(value.parent)),
    Object.fromEntries,
  ])(classRoles);
  return [classesDDL, rolesDDL];
};

export const getDeleteAudioErrorMessage = (e: any) => {
  if (isForbiddenError(e)) {
    return 'Unable to delete as the audio file has been set to private.';
  }
  if (isNotFoundError(e)) {
    return 'Unable to delete as the audio file no longer exists.';
  }
  return "Couldn't delete audio, please try again.";
};

const UPGRADE_LYRICS_GUID = 'LRC_upgradeMessage_58';

/*
 This is required to gracefully handle user migration to Lyrics v2.
 Static Lyric sheet will be returned to all clients which are using Lyrics v1.
 We ignore this static Lyric sheet for updated clients and let them use Lyrics v1.
 Old clients will be presented with static Lyric sheet and won't be able to edit it.
  */
export const filterUpgradeMessageLyrics = (lyrics?: LyricsData[]) =>
  (lyrics || []).filter((lyric) => lyric.guid !== UPGRADE_LYRICS_GUID);

export const getAddSongsMessage = (
  folderTitle = '',
  tryingToAddSongs: number,
  songsAdded: number
) => {
  const suffix = `to folder ${folderTitle}`;
  if (songsAdded === 0) {
    return `${
      tryingToAddSongs > 1 ? 'All songs' : 'Song'
    } already added ${suffix}`;
  }
  return `${songsAdded} ${songsAdded > 1 ? 'songs' : 'song'} added ${suffix}`;
};
